/**
 * Texte riche d'un champ `html` (ADR-146 §6.1, ERP-SEC-004) : un assainisseur à liste blanche écrit à
 * la main, sans dépendance. Le texte est lu par `DOMParser` (document inerte : aucun script exécuté,
 * aucune image chargée) puis réduit à un arbre de nœuds autorisés — même liste blanche que la tablette
 * (`rich_text_sanitizer.dart`) : paragraphes (`p`, `br`), gras (`b`, `strong`), italique (`i`, `em`),
 * listes (`ul`, `ol`, `li`), liens `https:` sans identifiants seulement — que React rend en éléments :
 * jamais d’insertion de HTML brut dans le DOM, aucun attribut recopié hormis l'adresse d'un lien vérifiée. Les
 * balises de code et d'objets embarqués partent avec leur contenu ; toute autre balise est retirée, son
 * texte reste ; un lien refusé (`javascript:` même encodé, `data:`, `http:`, relatif) devient du texte.
 */

export type RichTag = 'p' | 'br' | 'strong' | 'em' | 'ul' | 'ol' | 'li' | 'a';

export type RichNode = { kind: 'text'; text: string } | { kind: 'element'; tag: RichTag; href?: string; children: RichNode[] };

/** Balises gardées, et leur forme rendue (`b` devient `strong`, `i` devient `em`). */
const ALLOWED: Readonly<Record<string, RichTag>> = {
  p: 'p',
  div: 'p',
  br: 'br',
  strong: 'strong',
  b: 'strong',
  em: 'em',
  i: 'em',
  ul: 'ul',
  ol: 'ol',
  li: 'li',
  a: 'a',
};

/** Balises retirées avec tout leur contenu : la liste de la tablette (code, styles, objets embarqués). */
const DROPPED = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math', 'head', 'title', 'textarea', 'select']);

/** Profondeur au-delà de laquelle le texte est aplati (un texte piégé ne fait pas déborder la pile). */
const MAX_DEPTH = 16;

/**
 * Adresse d'un lien, comme la tablette (`safeHttpsUrl`) : `https:`, hôte présent, sans identifiants ni
 * caractère de contrôle ou espace ; toute autre (javascript:, data:, http:, relative) est refusée.
 * L'attribut arrive déjà décodé de ses entités (`&#106;avascript:`) par l'analyseur.
 */
export function safeHref(raw: string | null): string | undefined {
  if (raw === null) return undefined;
  const trimmed = raw.trim();
  // eslint-disable-next-line no-control-regex
  if (trimmed === '' || /[\u0000-\u0020\u007f]/.test(trimmed)) return undefined;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' && url.hostname !== '' && url.username === '' && url.password === '' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function textOf(node: Node): string {
  return node.textContent ?? '';
}

function convert(nodes: NodeListOf<ChildNode> | readonly ChildNode[], depth: number): RichNode[] {
  const out: RichNode[] = [];
  for (const node of Array.from(nodes)) {
    if (node.nodeType === 3) {
      const value = node.nodeValue ?? '';
      if (value !== '') out.push({ kind: 'text', text: value });
      continue;
    }
    if (node.nodeType !== 1) continue; // commentaires, instructions : retirés
    const element = node as Element;
    const name = element.localName.toLowerCase();
    if (DROPPED.has(name)) continue;
    if (depth >= MAX_DEPTH) {
      const flat = textOf(element);
      if (flat !== '') out.push({ kind: 'text', text: flat });
      continue;
    }
    const tag = ALLOWED[name];
    const children = convert(element.childNodes, depth + 1);
    if (tag === undefined) {
      // Balise inconnue (span, font, h1…) : son contenu seulement.
      out.push(...children);
    } else if (tag === 'br') {
      out.push({ kind: 'element', tag, children: [] });
    } else if (tag === 'a') {
      const href = safeHref(element.getAttribute('href'));
      // Lien refusé : le texte reste, sans lien.
      if (href === undefined) out.push(...children);
      else out.push({ kind: 'element', tag, href, children });
    } else {
      out.push({ kind: 'element', tag, children });
    }
  }
  return out;
}

/** Texte sans balise : une ligne par retour à la ligne. */
function plain(source: string): RichNode[] {
  const out: RichNode[] = [];
  source.split(/\r?\n/).forEach((line, index) => {
    if (index > 0) out.push({ kind: 'element', tag: 'br', children: [] });
    if (line !== '') out.push({ kind: 'text', text: line });
  });
  return out;
}

/** Arbre assaini d'un texte riche ; sans `DOMParser` (hors navigateur), le texte brut, balises comprises, jamais interprété. */
export function sanitizeRichText(source: string): RichNode[] {
  if (!/[<&]/.test(source)) return plain(source);
  if (typeof DOMParser === 'undefined') return plain(source);
  const document = new DOMParser().parseFromString(`<!doctype html><html><body>${source}</body></html>`, 'text/html');
  return convert(document.body.childNodes, 0);
}

/**
 * Champ d'affichage seul : widget `html` qui porte son texte (`presentation.content`, 1.9.0). Il n'a
 * jamais de réponse : ni valeur par défaut reprise, ni envoi au serveur (même règle que la tablette).
 */
export function isDisplayOnlyEntry(entry: { presentation?: unknown }): boolean {
  const presentation = entry.presentation as { widget?: string; content?: unknown } | undefined;
  return presentation?.widget === 'html' && presentation.content !== undefined && presentation.content !== null;
}
